/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IRequestContext } from '@msdyn365-commerce/core';
import { ProductRefinerValue } from '@msdyn365-commerce/retail-proxy';
import { IFindEngineHeaderSearchParams } from '../components/find-engine-heater';
import { ListPageState } from '../defa-search-result-container.list-page-state';

/**
 * Refiner Compression:
 * Refiners contains a non-trivial amount of data that is required to successfully make
 * a refiner-based API call. To allow for deep-linking of refiners in the URL, refiner data
 * must be compressed so that URL max length is not exceeded. Refiners will be compressed into
 * the following array style index-based format:
 * [DataTypeValue, LeftValueBoundString, RefinerRecordId, RefinerSourceValue, RightValueBoundString, UnitText].
 */
const compressedRefinerKeys = [
    'DataTypeValue',
    'LeftValueBoundString',
    'LeftValueBoundLocalizedString',
    'RefinerRecordId',
    'RefinerSourceValue',
    'RightValueBoundString',
    'RightValueBoundLocalizedString',
    'UnitText',
    // Added RowNumber to identify if custom range integer or API values option is selected
    'RowNumber'
];

/**
 * Hydrates compressed refiner data from URL.
 * @param fullUrl The full URL of the page.
 * @param requestContext
 */
export const hydrateRefinersFromUrl = (requestContext: IRequestContext): ProductRefinerValue[] => {
    const compressedRefinerArray = parseQueryParam<(string | number)[][]>('refiners', requestContext);
    if (!compressedRefinerArray || compressedRefinerArray.length === 0) {
        return [];
    }

    return <ProductRefinerValue[]>compressedRefinerArray
        .map(compressedRefiner => {
            try {
                const parsedRefiner: ProductRefinerValue = {};
                compressedRefinerKeys.forEach((refinerKey, index) => {
                    parsedRefiner[refinerKey] = compressedRefiner[index];
                });
                return parsedRefiner;
            } catch {
                return undefined;
            }
        })
        .filter(Boolean);
};

export const hydrateFindEngineSearchParamsFromUrl = (requestContext: IRequestContext): IFindEngineHeaderSearchParams | undefined => {
    const searchParams: Partial<IFindEngineHeaderSearchParams> = {};
    const compressedKeysInUrl = Object.values(findEngineSearchParamsMappings);

    compressedKeysInUrl.forEach(compressedKey => {
        const value = requestContext.url.requestUrl.searchParams.get(compressedKey);
        const originalKey = Object.entries(findEngineSearchParamsMappings).find(([, v]) => v === compressedKey)?.[0];
        if (!originalKey) {
            return;
        }
        searchParams[originalKey] = Number.isNaN(Number(value))
            ? value || undefined
            : Number(value);
    });

    if (searchParams.searchType === 'manual') {
        const hasAllRequiredParams = ['typeId', 'makeId', 'modelId'].every(key => searchParams[key]);
        if (hasAllRequiredParams) {
            return searchParams as IFindEngineHeaderSearchParams;
        }
    } else if (searchParams.searchType === 'registration' || searchParams.registrationNumber) {
        if (searchParams.registrationNumber) {
            searchParams.searchType = 'registration';
            return searchParams as IFindEngineHeaderSearchParams;
        }
    }

    return undefined;
};

/**
 * Compressed a list of refiners for use in URLs.
 * @param refiners The uncompressed refiners.
 */
export const compressRefiners = (refiners: ProductRefinerValue[]) => {
    const compressedRefiners: (string | number)[][] = [];

    refiners.forEach(refiner => {
        const compressedRefiner: (string | number)[] = [];
        compressedRefinerKeys.forEach(refinerKey => {
            compressedRefiner.push(refiner[refinerKey]);
        });
        compressedRefiners.push(compressedRefiner);
    });

    return compressedRefiners;
};

type FindEngineSearchParamKey = keyof Pick<IFindEngineHeaderSearchParams, 'searchType' | 'typeId' | 'makeId' | 'modelId' | 'engine' | 'modelSpec' | 'year' | 'registrationNumber'>;

const findEngineSearchParamsMappings: Record<FindEngineSearchParamKey, string> = {
    searchType: 'st',
    typeId: 't',
    makeId: 'ma',
    modelId: 'mo',
    engine: 'e',
    modelSpec: 'ms',
    year: 'y',
    registrationNumber: 'reg'
};

export const compressFindEngineSearchParams = (searchParams: IFindEngineHeaderSearchParams): Record<string, string> => {
    return Object.keys(searchParams).reduce((acc, key) => {
        const mappedKey = findEngineSearchParamsMappings[key as FindEngineSearchParamKey];
        if (mappedKey) {
            const value = searchParams[key as keyof IFindEngineHeaderSearchParams];
            if (value) {
                acc[mappedKey] = value.toString();
            }
        }
        return acc;
    }, {} as Record<string, string>);
};

/**
 * Method which creates a URL contiaining serialized active refiners.
 * @param fullUrl The Current URL of the page.
 * @param refiners The refiners which should be added to the URL.
 * @param sortingColumns
 * @param skipCount
 */
export const buildListPageUrl = (fullUrl: URL, listPageState: ListPageState) => {
    const refiners = listPageState.activeFilters || undefined;
    const sortingColumns = listPageState.sortingCritera?.Columns || undefined;

    // Refiner Change flow
    if (refiners !== undefined) {
        if (refiners.length === 0) {
            fullUrl.searchParams.delete('refiners');
        } else {
            const sortedRefiners = [...refiners].sort((first, second) =>
                first.RefinerRecordId && second.RefinerRecordId && first.RefinerRecordId > second.RefinerRecordId ? 1 : -1
            );
            fullUrl.searchParams.set('refiners', JSON.stringify(compressRefiners(sortedRefiners)));
        }
    }

    if (sortingColumns !== undefined) {
        if (sortingColumns.length === 0) {
            fullUrl.searchParams.delete('sorting');
        } else {
            fullUrl.searchParams.set('sorting', JSON.stringify(sortingColumns));
        }
    }

    if (listPageState.currentPageNumber !== null && listPageState.pageSize) {
        const skipCount = (listPageState.currentPageNumber - 1) * listPageState.pageSize;
        if (skipCount <= 0) {
            fullUrl.searchParams.delete('skip');
        } else {
            fullUrl.searchParams.set('skip', skipCount.toString());
        }
    }

    if (listPageState.searchParams) {
        const compressedSearchParams = compressFindEngineSearchParams(listPageState.searchParams);
        Object.keys(compressedSearchParams).forEach(key => {
            fullUrl.searchParams.set(key, compressedSearchParams[key]);
        });
    } else {
        Object.values(findEngineSearchParamsMappings).forEach(key => {
            fullUrl.searchParams.delete(key);
        });
    }

    return fullUrl.href;
};

/**
 * Parses a query param from the query object of the request context.
 * @param key The query parameter key.
 * @param requestContext The request context.
 */
export const parseQueryParam = <T>(key: string, requestContext: IRequestContext): T | null => {
    if (requestContext.query && requestContext.query[key]) {
        // NOTE: Query seems to already be decoded
        //return <T>JSON.parse(decodeURIComponent(requestContext.query[key]));

        return <T>JSON.parse(requestContext.query[key]);
    }
    return null;
};

/**
 * Gets the current URL of the page from the request context.
 * @param requestContext The request context.
 */
export const getCurrentUrl = (requestContext: IRequestContext): URL => {
    if (MsDyn365.isBrowser) {
        return new URL(window.location.href);
    }

    // NOTE: Typing on requestURL is incorrect
    if (requestContext.url.requestUrl.href) {
        return new URL(requestContext.url.requestUrl.href);
    }
    return new URL(<string>(<unknown>requestContext.url.requestUrl));
};
